import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Copy, Check, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { PAYMENT, FREE_SHIPPING_THRESHOLD, SKIN, WELLNESS, PEPTIDES, GLP1, UPSELL_BUNDLE } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n).toFixed(2)}`;

const UPSELL_IDS = ["nad-500", "ghkcu-100"];
const BEAUTY_IDS = new Set([...SKIN, ...WELLNESS].map((p) => p.id));
const PEPTIDE_IDS = new Set([...PEPTIDES.map((p) => p.id), ...GLP1.map((p) => p.id)]);

const METHODS = [
  { key: "venmo", label: "Venmo", handle: PAYMENT.venmo, color: "#008CFF", initial: "V" },
  { key: "cashapp", label: "Cash App", handle: PAYMENT.cashapp, color: "#00D632", initial: "$" },
  { key: "chime", label: "Chime", handle: PAYMENT.chime, color: "#1EC677", initial: "C" },
];

function Upsell() {
  const { items, addItem } = useCart();
  const qualifies = items.some((i) => BEAUTY_IDS.has(i.id) || PEPTIDE_IDS.has(i.id));
  if (!qualifies) return null;
  const inCart = new Set(items.map((i) => i.id));
  const hasBundle = inCart.has(UPSELL_BUNDLE.id);
  const hasAnyUpsell = UPSELL_BUNDLE.ids.some((id) => inCart.has(id));
  const showBundle = !hasBundle && !hasAnyUpsell;
  const picks = hasBundle ? [] : PEPTIDES.filter((p) => UPSELL_IDS.includes(p.id) && !inCart.has(p.id));
  if (!showBundle && picks.length === 0) return null;
  const savings = UPSELL_BUNDLE.original - UPSELL_BUNDLE.price;
  return (
    <div className="mt-8" data-testid="cart-upsell">
      <p className="overline text-[var(--ink-soft)] mb-3">Pairs well with</p>
      <div className="space-y-3">
        {showBundle && (
          <div className="relative border border-[var(--terra)] bg-[var(--terra)]/[0.04] p-3 flex items-center gap-3" data-testid="upsell-bundle">
            <span className="absolute -top-2 left-3 bg-[var(--terra)] text-white text-[10px] font-mono tracking-wide px-2 py-0.5 rounded-full">
              SAVE ${savings}
            </span>
            <div className="w-14 h-14 overflow-hidden shrink-0 bg-[var(--bone)] border border-[var(--line)]">
              <img src={UPSELL_BUNDLE.img} alt={UPSELL_BUNDLE.name} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-lg leading-tight">{UPSELL_BUNDLE.name}</h4>
              <p className="font-body text-xs text-[var(--ink-soft)] truncate">{UPSELL_BUNDLE.desc}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-body text-xs text-[var(--ink-soft)] line-through">{money(UPSELL_BUNDLE.original)}</div>
              <div className="font-display text-lg text-[var(--terra)] leading-none">{money(UPSELL_BUNDLE.price)}</div>
            </div>
            <button
              onClick={() => addItem({ id: UPSELL_BUNDLE.id, name: UPSELL_BUNDLE.name, variant: "2-vial set", price: UPSELL_BUNDLE.price, img: UPSELL_BUNDLE.img })}
              className="overline px-3 py-2 bg-[var(--terra)] text-white rounded-full hover:opacity-90 transition-opacity shrink-0"
              data-testid="upsell-add-bundle"
            >
              Add both
            </button>
          </div>
        )}
        {picks.map((p) => (
          <div key={p.id} className="flex items-center gap-3 border border-[var(--line)] p-3" data-testid={`upsell-${p.id}`}>
            <div className="w-14 h-14 overflow-hidden shrink-0 bg-[var(--bone)] border border-[var(--line)]">
              <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-lg leading-tight">{p.name}</h4>
              <p className="font-body text-xs text-[var(--ink-soft)] truncate">{p.desc}</p>
            </div>
            <span className="font-display text-lg shrink-0">{money(p.price)}</span>
            <button
              onClick={() => addItem({ id: p.id, name: p.name, variant: p.mg, price: p.price, img: p.img })}
              className="overline px-3 py-2 border border-[var(--ink)] rounded-full hover:bg-[var(--ink)] hover:text-[var(--bone)] transition-colors shrink-0"
              data-testid={`upsell-add-${p.id}`}
            >
              Add
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SaveCartRow() {
  const { items, subtotal } = useCart();
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const valid = /\S+@\S+\.\S+/.test(email);
  const save = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await axios.post(`${API}/cart/save`, {
        email,
        items: items.map((i) => ({ id: i.id, name: i.name, variant: i.variant, price: i.price, qty: i.qty })),
        subtotal,
      });
    } catch (e) {
      console.warn("Save cart failed:", e);
    } finally {
      setBusy(false);
      setSaved(true);
    }
  };
  if (saved) {
    return (
      <div className="mt-8 border border-[var(--line)] p-4 text-center" data-testid="save-cart-done">
        <p className="font-body text-sm text-[var(--ink)]">Saved — we'll email your bag if you don't finish. 💌</p>
      </div>
    );
  }
  return (
    <div className="mt-8 border border-[var(--line)] p-4" data-testid="save-cart">
      <p className="overline text-[var(--ink-soft)] mb-3">Not ready? Save your bag</p>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="flex-1 bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-2 font-body text-sm transition-colors"
          data-testid="save-cart-email"
        />
        <button
          onClick={save}
          disabled={!valid || busy}
          className="overline px-4 py-2 border border-[var(--ink)] rounded-full disabled:opacity-40 hover:bg-[var(--ink)] hover:text-[var(--bone)] transition-colors"
          data-testid="save-cart-btn"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      onClick={copy}
      className="w-full flex items-center justify-between border border-[var(--line)] px-4 py-4 hover:border-[var(--ink)] transition-colors"
      data-testid={testId}
    >
      <span className="text-left">
        <span className="overline text-[var(--ink-soft)] block">{label}</span>
        <span className="font-display text-2xl">{value}</span>
      </span>
      {copied ? <Check size={16} className="text-[var(--terra)]" /> : <Copy size={16} />}
    </button>
  );
}

function AddressAutocomplete({ form, setForm }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const timer = useRef(null);

  const lookup = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get("https://photon.komoot.io/api/", {
          params: { q, limit: 6, lang: "en" },
        });
        const feats = (data.features || []).filter(
          (f) => f.properties?.countrycode === "US" && (f.properties.street || f.properties.name)
        );
        setSuggestions(feats);
        setOpen(feats.length > 0);
      } catch (e) {
        console.warn("Address lookup failed:", e);
      }
    }, 300);
  };

  const pick = (f) => {
    const p = f.properties;
    const street = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
    setForm((prev) => ({
      ...prev,
      address: street || prev.address,
      city: p.city || p.town || p.village || p.county || prev.city,
      state: p.state || prev.state,
      zip: (p.postcode || "").replace(/\D/g, "").slice(0, 5) || prev.zip,
    }));
    setConfirmed(true);
    setOpen(false);
    setSuggestions([]);
  };

  const label = (p) =>
    [[p.housenumber, p.street || p.name].filter(Boolean).join(" "), p.city || p.town || p.village, p.state, p.postcode]
      .filter(Boolean)
      .join(", ");

  return (
    <label className="block relative">
      <span className="overline text-[var(--ink-soft)]">Street address</span>
      <input
        type="text"
        value={form.address}
        autoComplete="off"
        placeholder="Start typing your address…"
        onChange={(e) => { setForm({ ...form, address: e.target.value }); setConfirmed(false); lookup(e.target.value); }}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="mt-2 w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-3 pr-24 font-body text-lg placeholder:text-[var(--ink-soft)]/50"
        data-testid="checkout-address"
      />
      {confirmed && !open && (
        <span className="absolute right-0 bottom-3 flex items-center gap-1 text-[#1f9e96]" data-testid="address-confirmed">
          <Check size={16} />
          <span className="overline">Verified</span>
        </span>
      )}
      {open && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-[var(--bone)] border border-[var(--line)] shadow-lg max-h-60 overflow-y-auto thin-scroll" data-testid="address-suggestions">
          {suggestions.map((f, i) => (
            <li key={`${f.properties.osm_id || i}-${i}`}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(f); }}
                className="w-full text-left px-3 py-2.5 font-body text-sm hover:bg-[var(--line)]/40 transition-colors"
                data-testid={`address-suggestion-${i}`}
              >
                {label(f.properties)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, setQty, subtotal, shipping, discount, total, clear, promo, setPromo, promoValid } = useCart();
  const [view, setView] = useState("cart"); // cart | details | pay | confirmed
  const [method, setMethod] = useState("venmo");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", apt: "", city: "", state: "", zip: "" });
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [promoServer, setPromoServer] = useState(null); // null=unknown, true, false

  useEffect(() => {
    if (!promoValid || !/\S+@\S+\.\S+/.test(form.email)) { setPromoServer(null); return; }
    let cancelled = false;
    axios.post(`${API}/promo/validate`, { email: form.email, code: promo.trim().toUpperCase(), subtotal })
      .then(({ data }) => { if (!cancelled) setPromoServer(!!data.valid); })
      .catch(() => { if (!cancelled) setPromoServer(null); });
    return () => { cancelled = true; };
  }, [promoValid, form.email, promo, subtotal]);

  const promoAccepted = promoValid && promoServer !== false;
  const effDiscount = promoAccepted ? discount : 0;
  const effTotal = subtotal + shipping - effDiscount;

  const close = () => {
    setOpen(false);
    setTimeout(() => setView("cart"), 400);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const placeOrder = async () => {
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        items: items.map((i) => ({ id: i.id, name: i.name, variant: i.variant, price: i.price, qty: i.qty, ship_dose: i.ship_dose || null })),
        subtotal,
        total,
        payment_method: method,
        promo_code: promoValid ? promo.trim().toUpperCase() : null,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      setOrder(data);
      setView("pay");
    } catch (e) {
      console.error("Order creation failed:", e);
      setOrder({ reference: "PENDING", ...{} });
      setView("pay");
    } finally {
      setSubmitting(false);
    }
  };

  const detailsValid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.address.trim() && form.city.trim() && form.state.trim() && /^\d{5}$/.test(form.zip);
  const activeHandle = METHODS.find((m) => m.key === method)?.handle;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[10002] bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />
          <motion.aside
            className="fixed top-0 right-0 h-full w-full sm:w-[460px] z-[10003] bg-[var(--bone)] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: EASE }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[var(--line)]">
              {view !== "cart" && view !== "confirmed" ? (
                <button onClick={() => setView(view === "pay" ? "details" : "cart")} className="flex items-center gap-2 overline" data-testid="cart-back">
                  <ArrowLeft size={16} /> Back
                </button>
              ) : (
                <span className="font-display text-2xl">
                  {view === "confirmed" ? "Order placed" : "Your bag"}
                </span>
              )}
              <button onClick={close} className="w-10 h-10 rounded-full border border-[var(--line)] flex items-center justify-center" aria-label="Close" data-testid="cart-close">
                <X size={17} />
              </button>
            </div>

            {view !== "confirmed" && (
              <div className="bg-[var(--ink)] text-[var(--bone)] text-center py-2 px-4" data-testid="free-ship-banner">
                <span className="overline tracking-[0.2em]">🚚 Free shipping on orders over $200</span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto thin-scroll px-6 py-6">
              {/* CART */}
              {view === "cart" && (
                items.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-20">
                    <p className="font-display text-3xl">Your bag is empty.</p>
                    <p className="font-body text-[var(--ink-soft)] mt-3">Add something that works.</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {items.map((i) => (
                      <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.id}`}>
                        <div className="w-20 h-24 frame shrink-0">
                          <img src={i.img} alt={i.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between gap-2">
                            <h4 className="font-display text-xl leading-tight">{i.name}</h4>
                            <button onClick={() => removeItem(i.key)} className="text-[var(--ink-soft)] hover:text-[var(--terra)]" aria-label="Remove" data-testid={`cart-remove-${i.id}`}>
                              <X size={15} />
                            </button>
                          </div>
                          {i.variant && <p className="overline text-[var(--ink-soft)] mt-1">{i.variant}</p>}
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center border border-[var(--line)]">
                              <button onClick={() => setQty(i.key, i.qty - 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-dec-${i.id}`}><Minus size={13} /></button>
                              <span className="w-8 text-center font-mono text-sm">{i.qty}</span>
                              <button onClick={() => setQty(i.key, i.qty + 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-inc-${i.id}`}><Plus size={13} /></button>
                            </div>
                            <span className="font-display text-xl">{money(i.price * i.qty)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
              {view === "cart" && items.length > 0 && <Upsell />}
              {view === "cart" && items.length > 0 && <SaveCartRow />}

              {/* DETAILS */}
              {view === "details" && (
                <div className="space-y-5" data-testid="checkout-details">
                  <p className="font-body text-sm text-[var(--ink-soft)]">Where do we ship your order?</p>
                  <div className="border border-[var(--line)] p-4 space-y-1.5 text-sm font-body">
                    <div className="flex justify-between text-[var(--ink-soft)]"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                    <div className="flex justify-between text-[var(--ink-soft)]"><span>Shipping (flat)</span><span>{shipping === 0 ? "FREE" : money(shipping)}</span></div>
                    {effDiscount > 0 && <div className="flex justify-between text-[var(--terra)]" data-testid="details-discount"><span>Refill perk ({promo.toUpperCase()})</span><span>−{money(effDiscount)}</span></div>}
                    <div className="flex justify-between pt-1.5 border-t border-[var(--line)] text-[var(--ink)] font-medium"><span>Total</span><span data-testid="details-total">{money(effTotal)}</span></div>
                  </div>
                  {promoValid && promoServer === false && (
                    <p className="font-body text-xs text-[var(--terra)]" data-testid="promo-expired-note">
                      The {promo.toUpperCase()} code unlocks near your monthly refill date — it isn't active for this email right now.
                    </p>
                  )}
                  {[
                    { k: "name", l: "Full name", t: "text" },
                    { k: "email", l: "Email", t: "email" },
                    { k: "phone", l: "Phone (optional)", t: "tel" },
                  ].map((f) => (
                    <label key={f.k} className="block">
                      <span className="overline text-[var(--ink-soft)]">{f.l}</span>
                      <input
                        type={f.t}
                        value={form[f.k]}
                        onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                        className="mt-2 w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-3 font-body text-lg"
                        data-testid={`checkout-${f.k}`}
                      />
                    </label>
                  ))}
                  <AddressAutocomplete form={form} setForm={setForm} />
                  <label className="block">
                    <span className="overline text-[var(--ink-soft)]">Apt / Suite # (optional)</span>
                    <input
                      type="text"
                      value={form.apt}
                      onChange={(e) => setForm({ ...form, apt: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-3 font-body text-lg"
                      data-testid="checkout-apt"
                    />
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { k: "city", l: "City", t: "text" },
                      { k: "state", l: "State", t: "text" },
                      { k: "zip", l: "ZIP code", t: "text" },
                    ].map((f) => (
                      <label key={f.k} className="block">
                        <span className="overline text-[var(--ink-soft)]">{f.l}</span>
                        <input
                          type={f.t}
                          value={form[f.k]}
                          inputMode={f.k === "zip" ? "numeric" : undefined}
                          maxLength={f.k === "zip" ? 5 : undefined}
                          onChange={(e) => {
                            const v = f.k === "zip" ? e.target.value.replace(/\D/g, "").slice(0, 5) : e.target.value;
                            setForm({ ...form, [f.k]: v });
                          }}
                          className="mt-2 w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-3 font-body text-lg"
                          data-testid={`checkout-${f.k}`}
                        />
                      </label>
                    ))}
                  </div>
                  {form.zip && !/^\d{5}$/.test(form.zip) && (
                    <p className="font-body text-xs text-[var(--terra)]" data-testid="zip-error">Enter a valid 5-digit ZIP code.</p>
                  )}
                </div>
              )}

              {/* PAY */}
              {view === "pay" && (
                <div data-testid="checkout-pay">
                  <div className="border border-[var(--line)] p-5 mb-6">
                    <p className="overline text-[var(--ink-soft)]">Order reference</p>
                    <p className="font-display text-3xl mt-1">{order?.reference}</p>
                    <div className="mt-4 space-y-1.5 text-sm font-body">
                      <div className="flex justify-between text-[var(--ink-soft)]"><span>Subtotal</span><span>{money(order?.subtotal ?? subtotal)}</span></div>
                      <div className="flex justify-between text-[var(--ink-soft)]"><span>Shipping (flat)</span><span>{(order?.shipping ?? shipping) === 0 ? "FREE" : money(order?.shipping ?? shipping)}</span></div>
                      {(order?.discount ?? 0) > 0 && <div className="flex justify-between text-[var(--terra)]" data-testid="pay-discount"><span>Refill perk ({order?.promo_code})</span><span>−{money(order.discount)}</span></div>}
                      <div className="flex justify-between pt-1.5 border-t border-[var(--line)] text-[var(--ink)] font-medium"><span>Total to send</span><span data-testid="pay-total">{money(order?.total ?? total)}</span></div>
                    </div>
                    <p className="font-body text-xs text-[var(--ink-soft)] mt-3">
                      Send using any option below, then note your reference.
                    </p>
                  </div>
                  <p className="overline text-[var(--ink-soft)] mb-3">Choose how to pay</p>
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {METHODS.map((m) => (
                      <button
                        key={m.key}
                        onClick={() => setMethod(m.key)}
                        className={`flex flex-col items-center gap-2 py-4 rounded-xl border-2 transition-all ${method === m.key ? "border-[var(--ink)] shadow-sm" : "border-[var(--line)] opacity-70 hover:opacity-100"}`}
                        data-testid={`pay-tab-${m.key}`}
                      >
                        <span
                          className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--bone)] font-bold text-base"
                          style={{ background: m.color }}
                        >
                          {m.initial}
                        </span>
                        <span className="overline">{m.label}</span>
                      </button>
                    ))}
                  </div>
                  <CopyRow label={`${METHODS.find((m) => m.key === method)?.label} handle`} value={activeHandle} testId={`checkout-${method}`} />
                  <div className="mt-3">
                    <CopyRow label="Amount to send" value={money(order?.total ?? total)} testId="checkout-amount" />
                  </div>
                  <button
                    onClick={() => { clear(); setView("confirmed"); }}
                    className="btn-ink w-full py-4 rounded-full mt-6"
                    data-testid="checkout-paid-btn"
                  >
                    I've sent payment
                  </button>
                  <p className="font-mono text-xs text-[var(--ink-soft)] mt-4 text-center">
                    Wholesale &amp; MOQ: {PAYMENT.wholesaleEmail}
                  </p>
                </div>
              )}

              {/* CONFIRMED */}
              {view === "confirmed" && (
                <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="checkout-confirmed">
                  <div className="w-16 h-16 rounded-full bg-[var(--ink)] text-[var(--bone)] flex items-center justify-center">
                    <Check size={26} />
                  </div>
                  <h3 className="font-display text-4xl mt-6 leading-tight">Thank you.</h3>
                  <p className="font-body text-[var(--ink-soft)] mt-3 max-w-xs">
                    Your order <span className="text-[var(--ink)]">{order?.reference}</span> is reserved. We'll confirm shipping once payment lands.
                  </p>
                  <button onClick={close} className="btn-outline px-8 py-4 rounded-full mt-8" data-testid="confirmed-close">Continue browsing</button>
                </div>
              )}
            </div>

            {/* Footer actions */}
            {view === "cart" && items.length > 0 && (
              <div className="border-t border-[var(--line)] p-6">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-baseline">
                    <span className="overline text-[var(--ink-soft)]">Subtotal</span>
                    <span className="font-body text-lg" data-testid="cart-subtotal">{money(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="overline text-[var(--ink-soft)]">Shipping (flat)</span>
                    <span className="font-body text-lg" data-testid="cart-shipping">{shipping === 0 ? "FREE" : money(shipping)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between items-baseline" data-testid="cart-discount">
                      <span className="overline text-[var(--terra)]">Refill perk ({promo.toUpperCase()})</span>
                      <span className="font-body text-lg text-[var(--terra)]">−{money(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-[var(--line)]">
                    <span className="overline text-[var(--ink-soft)]">Total</span>
                    <span className="font-display text-3xl" data-testid="cart-total">{money(total)}</span>
                  </div>
                </div>
                {/* Promo code */}
                <div className="mb-4" data-testid="promo-row">
                  {promoValid ? (
                    <div className="flex items-center justify-between border border-[var(--terra)] bg-[var(--terra)]/[0.05] px-3 py-2.5 rounded-lg" data-testid="promo-applied">
                      <span className="font-body text-sm text-[var(--terra)]"><span className="font-medium">{promo.toUpperCase()}</span> applied — {money(discount)} off</span>
                      <button onClick={() => setPromo("")} className="overline text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="promo-remove">Remove</button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={promo}
                      onChange={(e) => setPromo(e.target.value)}
                      placeholder="Promo code"
                      className="w-full bg-transparent border border-[var(--line)] focus:border-[var(--ink)] outline-none px-3 py-2.5 rounded-lg font-body text-sm transition-colors"
                      data-testid="promo-input"
                    />
                  )}
                </div>
                {subtotal > 0 && (
                  <div className="mb-5" data-testid="free-ship-progress">
                    {subtotal < FREE_SHIPPING_THRESHOLD ? (
                      <p className="font-body text-xs text-[var(--ink-soft)] mb-2 text-center" data-testid="free-shipping-nudge">
                        You're <b className="text-[var(--terra)]">{money(FREE_SHIPPING_THRESHOLD - subtotal)}</b> away from free shipping
                      </p>
                    ) : (
                      <p className="font-body text-xs text-[#1f9e96] mb-2 text-center font-medium" data-testid="free-shipping-unlocked">
                        🎉 You've unlocked free shipping!
                      </p>
                    )}
                    <div className="h-2 rounded-full bg-[var(--line)] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{
                          width: `${Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100)}%`,
                          background: subtotal >= FREE_SHIPPING_THRESHOLD ? "#1f9e96" : "var(--terra)",
                        }}
                        data-testid="free-ship-progress-fill"
                      />
                    </div>
                  </div>
                )}
                {/* Accepted payment methods — visible upfront */}
                <div className="flex items-center gap-2 mb-4" data-testid="cart-accepted-methods">
                  <span className="overline text-[var(--ink-soft)] mr-1">We accept</span>
                  {METHODS.map((m) => (
                    <span
                      key={m.key}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[var(--bone)] text-xs font-medium"
                      style={{ background: m.color }}
                      data-testid={`accepted-${m.key}`}
                    >
                      <span className="font-bold">{m.initial}</span>{m.label}
                    </span>
                  ))}
                </div>
                <button onClick={() => setView("details")} className="btn-ink w-full py-4 rounded-full" data-testid="checkout-start-btn">
                  Checkout
                </button>
              </div>
            )}
            {view === "details" && (
              <div className="border-t border-[var(--line)] p-6">
                <button
                  disabled={!detailsValid || submitting}
                  onClick={placeOrder}
                  className="btn-ink w-full py-4 rounded-full disabled:opacity-40"
                  data-testid="checkout-topay-btn"
                >
                  {submitting ? "Creating order…" : `Continue to payment · ${money(effTotal)}`}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
